import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Modal,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getAllListData, ReplaceData } from 'services/CommonServices';
import { MASTER_ENDPOINT, SALES_ENDPOINT } from 'utils/Constant';

interface ShortlistPopupProps {
  open: boolean;
  onClose: () => void;
  onProceed?: (selectedItems: string[]) => void;
  candidateName?: string;
  jobTitle?: string;
  // attachmentsCount?: number;
  candidateInternalId?: string;
  getOverviewId?: {
    withGap: string;
    withoutGap: string;
  };
  jobMatchCandidateId?: string;
  candidateId?: string;
  candidateDetails?: any;
}

const modalStyle = {
  bgcolor: 'background.paper',
  borderRadius: '8px',
  boxShadow: 24,
  left: '50%',
  maxWidth: '90%',
  outline: 'none',
  position: 'absolute' as const,
  top: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
};

export const ShortlistPopup: React.FC<ShortlistPopupProps> = ({
  open,
  onClose,
  onProceed,
  candidateName,
  jobTitle,
  candidateInternalId,
  getOverviewId,
  jobMatchCandidateId,
  candidateId,
  candidateDetails,
}) => {
  const [selectedItems, setSelectedItems] = useState<string[]>([
    'resume',
    'overview',
    'attachments',
  ]);
  const params = useParams();
  const [attachments, setAttachments] = useState<any[]>([]);
  const [attachmentsCount, setAttachmentsCount] = useState<number>(0);
  const [attachmentsExpanded, setAttachmentsExpanded] = useState(false);
  const [selectedAttachments, setSelectedAttachments] = useState<Set<string>>(
    new Set(),
  );

  const getAttachments = () => {
    if (!params.candidateId) return;
    getAllListData(
      `${MASTER_ENDPOINT.Candidates}/attachments/files?candidateId=${params.candidateId}`,
    )
      .then((resp) => {
        const files = resp?.files || [];
        setAttachments(files);
        setAttachmentsCount(files.length || 0);
        const allIds: any = new Set(files.map((att: any) => String(att.id)));
        setSelectedAttachments(allIds);
        if (files.length > 0) {
          setSelectedItems((prev) =>
            prev.includes('attachments') ? prev : [...prev, 'attachments'],
          );
        }
        setAttachmentsExpanded(true);
      })
      .catch((err) => {
        setAttachments([]);
        setSelectedAttachments(new Set());
        setSelectedItems((prev) =>
          prev.filter((item) => item !== 'attachments'),
        );
      });
  };

  // Fetch attachments when modal opens
  useEffect(() => {
    if (open && params.candidateId) {
      getAttachments();
    } else {
      // Reset state when modal closes
      setAttachments([]);
      setAttachmentsExpanded(false);
      setSelectedAttachments(new Set());
      setSelectedItems(['resume', 'overview', 'attachments']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, params.candidateId]);

  // Calculate main checkbox state for attachments
  const getAttachmentsCheckboxState = () => {
    if (attachments?.length === 0) return false;
    const selectedCount = selectedAttachments.size;
    if (selectedCount === 0) return false;
    if (selectedCount === attachments?.length) return true;
    return 'indeterminate'; // Some but not all selected
  };

  const handleCheckboxChange = (item: string) => {
    setSelectedItems((prev) =>
      prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item],
    );
  };

  const handleAttachmentCheckboxChange = (attachmentId: string) => {
    setSelectedAttachments((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(attachmentId)) {
        newSet.delete(attachmentId);
      } else {
        newSet.add(attachmentId);
      }
      // Update selectedItems based on whether any attachments are selected
      if (newSet.size > 0) {
        setSelectedItems((prevItems) =>
          prevItems.includes('attachments')
            ? prevItems
            : [...prevItems, 'attachments'],
        );
      } else {
        setSelectedItems((prevItems) =>
          prevItems.filter((i) => i !== 'attachments'),
        );
      }
      return newSet;
    });
  };

  const handleMainAttachmentCheckboxChange = (checked: boolean) => {
    if (checked) {
      // Select all attachments
      const allIds = new Set(attachments.map((att) => String(att.id)));
      setSelectedAttachments(allIds);
      // Add 'attachments' to selectedItems if not already there
      if (!selectedItems.includes('attachments')) {
        setSelectedItems((prev) => [...prev, 'attachments']);
      }
    } else {
      // Deselect all attachments
      setSelectedAttachments(new Set());
      // Remove 'attachments' from selectedItems
      setSelectedItems((prev) => prev.filter((i) => i !== 'attachments'));
    }
  };

  const selectedDocuments = useMemo(() => {
    const docs: any[] = [];
    attachments.forEach((attachment) => {
      if (selectedAttachments.has(String(attachment.id))) {
        docs.push({
          file: attachment,
          fileName: attachment.file?.fileName || attachment.fileName,
          id: attachment.id,
          type: 'attachment',
        });
      }
    });

    return docs;
  }, [attachments, selectedAttachments]);

  const handleAttachmentsRowClick = (e: React.MouseEvent) => {
    // Don't toggle if clicking the checkbox
    if (
      (e.target as HTMLElement).closest('.MuiCheckbox-root') ||
      (e.target as HTMLElement).closest('input[type="checkbox"]')
    ) {
      return;
    }
    setAttachmentsExpanded(!attachmentsExpanded);
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleProceed = () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const payload: any = {
      candidateId: candidateId,
      docs: [
        ...(selectedItems.includes('resume')
          ? [
              {
                attachmentId: null,
                documentType: 'resume',
                fileId: candidateDetails?.resumeId,
                isSelected: true,
              },
            ]
          : []),
        ...(selectedItems.includes('overview')
          ? [
              {
                attachmentId: null,
                documentType: 'overviewSummary',
                fileId: getOverviewId?.withoutGap,
                isSelected: true,
              },
            ]
          : []),
        ...selectedDocuments.map((doc) => ({
          attachmentId: doc.id,
          documentType: doc.type,
          fileId: doc.file.fileId,
          isSelected: true,
        })),
      ],
      jobMatchCandidateId: jobMatchCandidateId,
      sourceQueue: 'benchQueue',
    };
    ReplaceData(params.jobId || '', payload, SALES_ENDPOINT.SHORTLIST, false)
      .then((resp: any) => {
        toast.success('Candidate has been shortlisted successfully.');
        if (onProceed) {
          onProceed(selectedItems);
        }
        onClose();
      })
      .catch((err: any) => {})
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="select-items-modal-title"
      aria-describedby="select-items-modal-description">
      <Box sx={modalStyle}>
        {/* Close Button */}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            '&:hover': {
              backgroundColor: '#f3f4f6',
            },
            color: '#6b7280',
            position: 'absolute',
            right: 8,
            top: 8,
          }}>
          <CloseIcon />
        </IconButton>

        {/* Title */}
        <Typography
          id="select-items-modal-title"
          sx={{
            color: '#242424',
            fontFamily: 'Inter',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: 600,
            letterSpacing: '0%',
            lineHeight: '22px',
            mb: '4px',
            pt: '24px',
            px: '24px',
          }}>
          Select Items to Include
        </Typography>

        {/* Subtitle */}
        <Typography
          sx={{
            color: '#707070',
            fontFamily: 'Inter',
            fontSize: '13px',
            fontStyle: 'normal',
            fontWeight: 400,
            letterSpacing: '0%',
            lineHeight: '20px',
            mb: '24px',
            px: '24px',
          }}>
          These are the final documents being submitted.
        </Typography>

        {/* Checkbox List */}
        <Box
          sx={{
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            paddingBottom: '24px',
            paddingTop: '16px',
            paddingX: '24px',
          }}>
          {/* Resume */}
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedItems.includes('resume')}
                onChange={() => handleCheckboxChange('resume')}
                sx={{
                  '&.Mui-checked': {
                    color: '#2d2d2d',
                  },
                }}
              />
            }
            label={
              <Typography
                sx={{
                  color: '#242424',
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '20px',
                }}>
                Resume
              </Typography>
            }
            sx={{
              marginLeft: 0,
            }}
          />

          {/* Candidate Overview Summary */}
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedItems.includes('overview')}
                onChange={() => handleCheckboxChange('overview')}
                sx={{
                  '&.Mui-checked': {
                    color: '#2d2d2d',
                  },
                }}
              />
            }
            label={
              <Typography
                sx={{
                  color: '#242424',
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '20px',
                }}>
                Candidate Overview Summary
              </Typography>
            }
            sx={{
              marginLeft: 0,
            }}
          />

          {/* All Attachments */}
          <Box
            onClick={handleAttachmentsRowClick}
            sx={{
              alignItems: 'center',
              cursor: 'pointer',
              display: 'flex',
              gap: '6px',
              width: '100%', // Horizontal padding for the header row
            }}>
            <Checkbox
              checked={getAttachmentsCheckboxState() === true}
              indeterminate={getAttachmentsCheckboxState() === 'indeterminate'}
              onChange={(e) =>
                handleMainAttachmentCheckboxChange(e.target.checked)
              }
              onClick={(e) => e.stopPropagation()}
              sx={{
                '&.Mui-checked': {
                  color: '#2d2d2d',
                },
                '&.MuiCheckbox-indeterminate': {
                  color: '#2d2d2d',
                },
              }}
            />
            <Typography
              sx={{
                color: '#242424',
                flex: 1,
                fontFamily: 'Inter',
                fontSize: '14px',
                fontWeight: 500, // Slightly bolder text for the header like in the image
                lineHeight: '20px',
              }}>
              All Attachments ({attachments?.length || attachmentsCount} files)
            </Typography>
            {attachmentsExpanded ? (
              <ExpandLessIcon
                sx={{
                  color: '#616161',
                  fontSize: '16px',
                }}
              />
            ) : (
              <ExpandMoreIcon
                sx={{
                  color: '#616161',
                  fontSize: '16px',
                }}
              />
            )}
          </Box>
          {attachmentsExpanded && (
            <Box
              sx={{
                backgroundColor: 'rgb(250, 250, 251)',
                border: '1px solid #eeeeee', // Subtle border
                borderRadius: '4px',
                padding: '8px 0', // Subtle shadow
                width: '100%',
              }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0px',
                  }}>
                  {attachments && attachments?.length > 0 ? (
                    attachments?.map((attachment) => (
                      <FormControlLabel
                        key={attachment.id}
                        control={
                          <Checkbox
                            checked={selectedAttachments.has(
                              String(attachment.id),
                            )}
                            onChange={() =>
                              handleAttachmentCheckboxChange(
                                String(attachment.id),
                              )
                            }
                            sx={{
                              '&.Mui-checked': {
                                color: '#2d2d2d',
                              },
                            }}
                          />
                        }
                        label={
                          <Typography
                            sx={{
                              color: '#242424',
                              display: 'inline-block',
                              fontFamily: 'Inter',
                              fontSize: '14px',
                              fontWeight: 400,
                              lineHeight: '20px',
                              maxWidth: '400px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              verticalAlign: 'middle',
                              whiteSpace: 'nowrap',
                              wordBreak: 'break-word',
                            }}>
                            {attachment.file?.fileName || attachment.fileName}
                          </Typography>
                        }
                        sx={{
                          // Applied horizontal padding for list items
                          // Adjusted margin to align with the header text
                          '& .MuiFormControlLabel-label': {
                            marginLeft: '-8px', // Pull the label slightly left
                          },

                          marginLeft: 0,

                          padding: '0 12px',
                        }}
                      />
                    ))
                  ) : (
                    <Typography
                      sx={{
                        alignItems: 'center',
                        color: '#242424',
                        display: 'flex',
                        fontFamily: 'Inter',
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '20px',
                        marginLeft: '10px',
                        maxWidth: '400px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        verticalAlign: 'middle',
                        whiteSpace: 'nowrap',
                        wordBreak: 'break-word',
                      }}>
                      {'No attachments found.'}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            gap: '6px',
            justifyContent: 'flex-end',
            padding: '12px 24px',
          }}>
          <Button
            onClick={handleCancel}
            sx={{
              '&:hover': {
                backgroundColor: '#f9fafb',
              },
              backgroundColor: '#ffffff',
              border: '1px solid #d1d1d1',
              borderRadius: '6px',
              color: '#242424',
              fontFamily: 'Inter',
              fontSize: '12px',
              fontWeight: 600,
              lineHeight: '20px',
              padding: '6px 12px',
              textTransform: 'none',
            }}>
            Cancel
          </Button>
          <Button
            disabled={isSubmitting}
            onClick={handleProceed}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={16} sx={{ color: '#ffffff' }} />
              ) : null
            }
            sx={{
              '&.Mui-disabled': {
                backgroundColor: '#2d2d2d',
                color: '#ffffff',
                opacity: 0.8,
              },
              '&:hover': {
                backgroundColor: '#2d2d2d',
              },
              backgroundColor: '#2d2d2d',
              borderRadius: '6px',
              color: '#ffffff',
              fontFamily: 'Inter',
              fontSize: '12px',
              fontWeight: 600,
              lineHeight: '20px',
              padding: '6px 12px',
              textTransform: 'none',
            }}>
            {isSubmitting ? 'Proceeding...' : 'Proceed'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
